import React from 'react'
import SEO from '../components/SEO'

const ModernSlavery = () => {
  return (
    <div className="pt-20">
      <SEO 
        title="Modern Slavery Statement - Nexus Recruitment Solutions - International Healthcare & Nursing Jobs Overseas"
        description="Our commitment to combating modern slavery and human trafficking in all its forms. Read Nexus Recruitment Solutions' modern slavery statement."
        canonical="/modern-slavery"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-accent-600 text-white section-padding">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6">
              Modern Slavery & Human Trafficking Statement
            </h1>
            <p className="text-xl text-primary-100">
              Our commitment to combating modern slavery and human trafficking in all its forms
            </p>
          </div>
        </div>
      </section>

      {/* Statement Content */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <div className="bg-gray-50 rounded-lg p-8">
              <h2 className="text-2xl font-heading font-bold text-gray-900 mb-6">
                Modern Slavery & Human Trafficking Statement
              </h2>
              
              <div className="space-y-6 text-gray-700">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">1. Introduction</h3>
                  <p>
                    This statement is made pursuant to the Modern Slavery Act 2015 and sets out the steps that {import.meta.env.COMPANY_NAME} Recruitment Solutions has taken and continues to take to ensure that modern slavery or human trafficking is not taking place within our business or supply chain.
                  </p>
                  <p className="mt-3">
                    Modern slavery encompasses slavery, servitude, human trafficking, and forced labor. {import.meta.env.COMPANY_NAME} Recruitment Solutions has a zero-tolerance approach to any form of modern slavery and is committed to acting ethically and with integrity in all business dealings and to putting effective systems and controls in place to safeguard against any form of modern slavery taking place within the business or our supply chain.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">2. Our Business and Supply Chain</h3>
                  <p>
                    {import.meta.env.COMPANY_NAME} Recruitment Solutions is an international recruitment agency specializing in healthcare recruitment. We connect qualified healthcare professionals with rewarding career opportunities worldwide, with a particular focus on nursing and allied health positions.
                  </p>
                  <p className="mt-3">
                    Our supply chain includes:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Healthcare institutions and employers</li>
                    <li>Immigration and visa processing services</li>
                    <li>Professional licensing and certification bodies</li>
                    <li>Technology and software providers</li>
                    <li>Professional services providers</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">3. Our Policies</h3>
                  <p>
                    We have implemented several policies that demonstrate our commitment to identifying and preventing modern slavery:
                  </p>
                  
                  <h4 className="text-lg font-medium text-gray-900 mt-4 mb-2">Ethical Recruitment Policy</h4>
                  <p>
                    We strictly adhere to the International Labour Organization (ILO) conventions and the International Code of Ethical Recruitment. Our recruitment practices ensure:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>No recruitment fees are charged to candidates</li>
                    <li>Transparent communication throughout the process</li>
                    <li>Fair and ethical treatment of all candidates</li>
                    <li>Respect for workers' rights and dignity</li>
                  </ul>

                  <h4 className="text-lg font-medium text-gray-900 mt-4 mb-2">Supplier Code of Conduct</h4>
                  <p>
                    We require all suppliers and partners to:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Comply with all applicable laws and regulations</li>
                    <li>Maintain ethical business practices</li>
                    <li>Respect human rights and worker dignity</li>
                    <li>Prohibit the use of forced, bonded, or child labor</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">4. Due Diligence and Risk Assessment</h3>
                  <p>
                    We conduct thorough due diligence on all our business partners and suppliers to ensure they meet our ethical standards. This includes:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Background checks on all partner organizations</li>
                    <li>Regular audits and assessments of our supply chain</li>
                    <li>Verification of employment practices and working conditions</li>
                    <li>Ongoing monitoring of compliance with our standards</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">5. Training and Awareness</h3>
                  <p>
                    We provide regular training to our staff to ensure they understand:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>The risks of modern slavery in recruitment</li>
                    <li>How to identify potential signs of modern slavery</li>
                    <li>The importance of ethical recruitment practices</li>
                    <li>How to report concerns or suspicions</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">6. Reporting and Whistleblowing</h3>
                  <p>
                    We encourage the reporting of concerns and maintain several channels for reporting suspected modern slavery or human trafficking:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Direct reporting to management</li>
                    <li>Anonymous reporting through our whistleblowing policy</li>
                    <li>External reporting to relevant authorities</li>
                  </ul>
                  <p className="mt-3">
                    We are committed to ensuring no one suffers any detrimental treatment as a result of reporting in good faith their suspicion that modern slavery is or may be taking place.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">7. Our Effectiveness in Combating Modern Slavery</h3>
                  <p>
                    We measure the effectiveness of our efforts through:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Regular audits of our recruitment processes</li>
                    <li>Feedback from candidates and partners</li>
                    <li>Compliance monitoring and reporting</li>
                    <li>Continuous improvement of our policies and procedures</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">8. Further Steps</h3>
                  <p>
                    We are committed to continuously improving our approach to combating modern slavery. Our future steps include:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Enhanced due diligence procedures</li>
                    <li>Expanded training programs</li>
                    <li>Strengthened monitoring and reporting systems</li>
                    <li>Increased collaboration with industry partners and authorities</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">9. Contact Information</h3>
                  <p>
                    If you have any concerns about modern slavery or human trafficking in relation to our business, please contact us at:
                  </p>
                  <div className="mt-2">
                    <p><strong>Email:</strong> info@nexusrecruitmentsolutions.com</p>
                    <p><strong>Phone:</strong> UK: +44 (0) 7771 646247 | India: +91 9600138613</p>
                    <p><strong>Address:</strong> Horsefair House 7-8 Sibford Road Hook Norton, Oxfordshire OX15 5LA, United Kingdom</p>
                  </div>
                </div>

                <div className="border-t pt-6 mt-8">
                  <p className="text-sm text-gray-600">
                    This statement is made pursuant to section 54(1) of the Modern Slavery Act 2015 and constitutes our slavery and human trafficking statement for the financial year ending 2024.
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    <strong>Last updated:</strong> {new Date().toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default ModernSlavery
